import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { getApiError } from '@/configs/axios.config'
import type { OgsmApi } from '@/features/ogsm/ogsm-api'
import { applyMove, type OgsmMove } from '@/features/ogsm/reorder'
import type {
  CreateOgsmActualInput,
  CreateOgsmNodeInput,
  CreateOgsmRevisionInput,
  OgsmBoard,
  OgsmBoardNode,
  OgsmNode,
  UpdateOgsmNodeInput,
} from '@/types/api'

/** The query keys of one OGSM scope; for a company they equal `ogsmKeys.*(companyId, …)`. */
export function ogsmScopeKeys(api: OgsmApi) {
  return {
    sessions: [...api.key, 'sessions'] as const,
    board: (sessionId: string) => [...api.key, 'board', sessionId] as const,
    assignees: [...api.key, 'assignees'] as const,
    actuals: (nodeId: string) => [...api.key, 'actuals', nodeId] as const,
    removalImpact: (nodeId: string) =>
      [...api.key, 'removal-impact', nodeId] as const,
  }
}

export function useOgsmSessionsQuery(api: OgsmApi, enabled = true) {
  return useQuery({
    queryKey: ogsmScopeKeys(api).sessions,
    queryFn: () => api.listSessions(),
    enabled,
  })
}

export function useOgsmBoardQuery(api: OgsmApi, sessionId: string | undefined) {
  return useQuery({
    queryKey: ogsmScopeKeys(api).board(sessionId ?? ''),
    queryFn: () => api.getBoard(sessionId ?? ''),
    enabled: Boolean(sessionId),
  })
}

/**
 * The board of the session in the URL, or of the newest session when the URL
 * names none. The page header and the workspace both use it and share one
 * cache entry, so the extra reader costs no request.
 */
export function useActiveOgsmBoardQuery(
  api: OgsmApi,
  sessionId: string | undefined
) {
  const sessions = useOgsmSessionsQuery(api)
  const activeId = sessionId ?? sessions.data?.sessions[0]?.id
  const board = useOgsmBoardQuery(api, activeId)
  return { sessions, board, activeId }
}

export function useOgsmAssigneesQuery(api: OgsmApi, enabled = true) {
  return useQuery({
    queryKey: ogsmScopeKeys(api).assignees,
    queryFn: () => api.listAssignees(),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Applies an edit to one node wherever it sits in the tree. The backend nests
 * strategies down to its configured depth, so this recurses instead of
 * assuming a number of levels.
 */
function patchNode(
  nodes: OgsmBoardNode[],
  nodeId: string,
  patch: Partial<OgsmBoardNode>
): OgsmBoardNode[] {
  return nodes.map((node) => {
    const patched = node.id === nodeId ? { ...node, ...patch } : node
    return {
      ...patched,
      strategies: patchNode(patched.strategies, nodeId, patch),
    }
  })
}

function invalidateBoard(
  queryClient: QueryClient,
  api: OgsmApi,
  sessionId: string
) {
  return queryClient.invalidateQueries({
    queryKey: ogsmScopeKeys(api).board(sessionId),
  })
}

function invalidateSessions(queryClient: QueryClient, api: OgsmApi) {
  return queryClient.invalidateQueries({
    queryKey: ogsmScopeKeys(api).sessions,
  })
}

/** The Scorecard reads node statuses and actuals; a scope without one skips it. */
function invalidateScorecard(queryClient: QueryClient, api: OgsmApi) {
  return api.scorecardKey === null
    ? Promise.resolve()
    : queryClient.invalidateQueries({ queryKey: api.scorecardKey })
}

export function useCreateOgsmSessionMutation(api: OgsmApi) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.createSession(),
    onSuccess: () => invalidateSessions(queryClient, api),
  })
}

export function useUpdateOgsmStatementMutation(
  api: OgsmApi,
  sessionId: string
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (statement: string | null) =>
      api.updateStatement(sessionId, statement),
    onSettled: () => invalidateBoard(queryClient, api, sessionId),
  })
}

export function useCreateOgsmNodeMutation(api: OgsmApi, sessionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateOgsmNodeInput) =>
      api.createNode(sessionId, input),
    onSuccess: () =>
      Promise.all([
        invalidateBoard(queryClient, api, sessionId),
        invalidateSessions(queryClient, api),
      ]),
  })
}

export function useUpdateOgsmNodeMutation(api: OgsmApi, sessionId: string) {
  const queryClient = useQueryClient()
  const key = ogsmScopeKeys(api).board(sessionId)
  return useMutation({
    mutationFn: ({
      nodeId,
      input,
    }: {
      nodeId: string
      input: UpdateOgsmNodeInput
    }) => api.updateNode(nodeId, input),
    onMutate: async ({ nodeId, input }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<OgsmBoard>(key)
      if (previous) {
        queryClient.setQueryData<OgsmBoard>(key, {
          ...previous,
          objectives: patchNode(previous.objectives, nodeId, input),
        })
      }
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    // Status changes reach the linked Scorecard KPI, so its rows refresh too.
    onSettled: () =>
      Promise.all([
        invalidateBoard(queryClient, api, sessionId),
        invalidateScorecard(queryClient, api),
      ]),
  })
}

/**
 * Reorders a node among its siblings. The backend answers with the one node,
 * not the board, so the settle refetch is what shows the persisted order.
 */
export function useMoveOgsmNodeMutation(api: OgsmApi, sessionId: string) {
  const queryClient = useQueryClient()
  const key = ogsmScopeKeys(api).board(sessionId)
  return useMutation<OgsmNode, Error, OgsmMove, { previous?: OgsmBoard }>({
    mutationFn: ({ nodeId, position }) => api.moveNode(nodeId, position),
    onMutate: async (move) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<OgsmBoard>(key)
      if (previous) {
        queryClient.setQueryData<OgsmBoard>(key, applyMove(previous, move))
      }
      return { previous }
    },
    onError: (_error, _move, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    // A reorder changes no status or Scorecard row, so only the board refetches.
    onSettled: () => invalidateBoard(queryClient, api, sessionId),
  })
}

export function useDeleteOgsmNodeMutation(api: OgsmApi, sessionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (nodeId: string) => api.deleteNode(nodeId),
    onSuccess: () =>
      Promise.all([
        invalidateBoard(queryClient, api, sessionId),
        invalidateSessions(queryClient, api),
      ]),
  })
}

/**
 * Read when the delete dialog opens. The dialog mounts it only while open and
 * gcTime 0 drops it on close, so every open starts fresh.
 */
export function useOgsmRemovalImpactQuery(api: OgsmApi, nodeId: string) {
  return useQuery({
    queryKey: ogsmScopeKeys(api).removalImpact(nodeId),
    queryFn: () => api.getRemovalImpact(nodeId),
    enabled: Boolean(nodeId),
    staleTime: 0,
    gcTime: 0,
  })
}

/** Same refetches as a delete; the Scorecard keeps its earlier entries (GSCFE-177). */
export function useArchiveOgsmNodeMutation(api: OgsmApi, sessionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (nodeId: string) => api.archiveNode(nodeId),
    onSuccess: () =>
      Promise.all([
        invalidateBoard(queryClient, api, sessionId),
        invalidateSessions(queryClient, api),
      ]),
  })
}

export function useOgsmActualsQuery(
  api: OgsmApi,
  nodeId: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: ogsmScopeKeys(api).actuals(nodeId),
    queryFn: () => api.listActuals(nodeId),
    enabled: enabled && Boolean(nodeId),
  })
}

export function useAddOgsmActualMutation(api: OgsmApi, sessionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      nodeId,
      input,
    }: {
      nodeId: string
      input: CreateOgsmActualInput
    }) => api.addActual(nodeId, input),
    // A metric-backed actual is the Scorecard's own row, so it refreshes too.
    onSuccess: (_actual, { nodeId }) =>
      Promise.all([
        invalidateBoard(queryClient, api, sessionId),
        queryClient.invalidateQueries({
          queryKey: ogsmScopeKeys(api).actuals(nodeId),
        }),
        invalidateScorecard(queryClient, api),
      ]),
  })
}

export function useReviseOgsmTargetMutation(api: OgsmApi, sessionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      nodeId,
      input,
    }: {
      nodeId: string
      input: CreateOgsmRevisionInput
    }) => api.reviseTarget(nodeId, input),
    // A 409 means the node changed since the board loaded; refetch so the dialog's flags catch up.
    onSettled: (_node, error) =>
      error && getApiError(error).statusCode !== 409
        ? undefined
        : Promise.all([
            invalidateBoard(queryClient, api, sessionId),
            invalidateScorecard(queryClient, api),
          ]),
  })
}
